import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { StyleSheet, View, ActivityIndicator, TextInput, Pressable, Platform } from 'react-native';
import { router } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import SettingsSection from '@/components/SettingsSection';
import SwitchInput from '@/components/Inputs/Switch';
import { useToastStore, useAuthStore, useMemoryStore, useModalStore } from '@/store';
import { accountApi } from '@/lib/api';
import MemoryItem from '@/components/Settings/MemoryItem';
import moment from 'moment-mini';
import { MEMORY_CHAR_LIMIT } from '@/constants/config';

const formatDate = (dateString) => {
  return moment(dateString).format('MMM D, YYYY');
};

const CHAR_COUNT_WARNING_COLOR = '#F59E0B';

function getCharCountProps(length, limit) {
  const ratio = length / limit;
  if (ratio > 0.9) return { color: 'danger', style: undefined };
  if (ratio > 0.75) return { color: undefined, style: { color: CHAR_COUNT_WARNING_COLOR } };
  return { color: 'secondary', style: undefined };
}

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyState: {
    textAlign: 'center',
    paddingVertical: 40,
  },
  toggleSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  toggleLabel: {
    flex: 1,
    marginRight: 12,
  },
  memoryList: {
    width: '100%',
    marginTop: 10,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    borderRadius: 8,
    overflow: 'hidden',
  },
  addMemorySection: {
    marginBottom: 8,
    width: '100%',
  },
  inputWrapper: {
    marginBottom: 8,
  },
  memoryInput: {
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
    color: colors.text_color_primary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    minHeight: 84,
    textAlignVertical: 'top',
  },
  characterCount: {
    textAlign: 'right',
    marginTop: 6,
  },
  divider: {
    borderColor: colors.border_color_secondary,
    borderWidth: 0.5,
    marginTop: 5,
    marginBottom: 20,
  },
  plusUpsell: {
    marginTop: 8,
  },
});

export default function MemorySettings() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  
  const updateUserPreferences = useAuthStore(state => state.updateUserPreferences);
  const userMemoryPreferences = useAuthStore(state => state.user?.preferences?.memory);
  const isPlus = useAuthStore(state => !!state.user?.flags?.isPlus);
  
  // Memory store - state selectors
  const memoriesMap = useMemoryStore(state => state.memories);
  const memoryIds = useMemoryStore(state => state.memoryIds);
  const isLoading = useMemoryStore(state => state.isLoading.fetch);
  const fetchError = useMemoryStore(state => state.errors.fetch);
  const isCreating = useMemoryStore(state => state.isLoading.create);

  // Memory store - actions
  const fetchMemories = useMemoryStore(state => state.fetchMemories);
  const createMemory = useMemoryStore(state => state.createMemory);
  const updateMemory = useMemoryStore(state => state.updateMemory);
  const deleteMemory = useMemoryStore(state => state.deleteMemory);

  // Derive memories list from map and ids using useMemo
  const memories = useMemo(() => {
    return memoryIds.map(id => memoriesMap[id]).filter(Boolean);
  }, [memoriesMap, memoryIds]);
  
  const [newMemory, setNewMemory] = useState('');
  const [isMemoryEnabled, setIsMemoryEnabled] = useState(userMemoryPreferences?.isEnabled !== false);
  const [isUpdatingPreference, setIsUpdatingPreference] = useState(false);
  // One "learn from conversations" switch → three backend flags in lockstep
  // (matches Memory page + /json/user/memory-preferences).
  const [isLearningEnabled, setIsLearningEnabled] = useState(
    userMemoryPreferences?.isAutoGenerationEnabled !== false
    && userMemoryPreferences?.autoSaveEnabled !== false
    && userMemoryPreferences?.dreamingEnabled !== false
  );
  const [isUpdatingLearning, setIsUpdatingLearning] = useState(false);

  const styles = useMemo(() => createStyles(colors), [colors]);

  useEffect(() => {
    fetchMemories().catch((err) => {
      console.error('Error fetching memories:', err);
      addToast(t('settings.memory.failedToLoad'), 'error');
    });
  }, [fetchMemories, addToast]);

  useEffect(() => {
    setIsMemoryEnabled(userMemoryPreferences?.isEnabled !== false);
    setIsLearningEnabled(
      userMemoryPreferences?.isAutoGenerationEnabled !== false
      && userMemoryPreferences?.autoSaveEnabled !== false
      && userMemoryPreferences?.dreamingEnabled !== false
    );
  }, [
    userMemoryPreferences?.isEnabled,
    userMemoryPreferences?.isAutoGenerationEnabled,
    userMemoryPreferences?.autoSaveEnabled,
    userMemoryPreferences?.dreamingEnabled,
  ]);

  const handleMemoryToggle = useCallback(async (value) => {
    try {
      setIsUpdatingPreference(true);
      setIsMemoryEnabled(value);

      await accountApi.updateMemoryPreferences(value, undefined);

      updateUserPreferences({
        memory: {
          isEnabled: value
        }
      });

      addToast(
        value ? t('settings.memory.memoryEnabled') : t('settings.memory.memoryDisabledMsg'),
        'success'
      );
    } catch (error) {
      console.error('Error updating memory preference:', error);
      setIsMemoryEnabled(!value);
      addToast(t('settings.memory.memoryPrefFailed'), 'error');
    } finally {
      setIsUpdatingPreference(false);
    }
  }, [t, updateUserPreferences, addToast]);

  const handlePlusUpsell = useCallback(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android' || Platform.OS === 'macos') {
      router.push('/(auth)/paywall');
      return;
    }
    openModal('PricingModal', {
      title: t('settings.memory.learnPlusUpsell'),
      subtitle: t('settings.memory.learnFromConversationsDescFree'),
    });
  }, [openModal, t]);

  const handleLearningToggle = useCallback(async (value) => {
    if (!isPlus) {
      handlePlusUpsell();
      return;
    }
    try {
      setIsUpdatingLearning(true);
      setIsLearningEnabled(value);

      await accountApi.updateMemoryPreferences(undefined, value, value, value);

      updateUserPreferences({
        memory: {
          isAutoGenerationEnabled: value,
          autoSaveEnabled: value,
          dreamingEnabled: value,
        }
      });

      addToast(
        value ? t('settings.memory.learnEnabled') : t('settings.memory.learnDisabled'),
        'success'
      );
    } catch (error) {
      console.error('Error updating learning preference:', error);
      setIsLearningEnabled(!value);
      addToast(t('settings.memory.autoGenFailed'), 'error');
    } finally {
      setIsUpdatingLearning(false);
    }
  }, [isPlus, handlePlusUpsell, t, updateUserPreferences, addToast]);

  const handleCreateMemory = useCallback(async () => {
    if (!newMemory.trim()) {
      addToast(t('settings.memory.memoryEmpty'), 'error');
      return;
    }

    if (newMemory.length > MEMORY_CHAR_LIMIT) {
      addToast(t('settings.memory.charLimit').replace('%LIMIT%', MEMORY_CHAR_LIMIT), 'error');
      return;
    }

    try {
      await createMemory(newMemory.trim());
      setNewMemory('');
      addToast(t('settings.memory.memoryCreated'), 'success');
    } catch (error) {
      console.error('Error creating memory:', error);
      addToast(t('settings.memory.memoryCreateFailed'), 'error');
    }
  }, [newMemory, createMemory, addToast]);

  const handleUpdateMemory = useCallback(async (memoryId, content) => {
    try {
      await updateMemory(memoryId, content);
      addToast(t('settings.memory.memoryUpdated'), 'success');
      return true;
    } catch (error) {
      console.error('Error updating memory:', error);
      addToast(t('settings.memory.memoryUpdateFailed'), 'error');
      return false;
    }
  }, [updateMemory, addToast]);

  const handleDeleteMemory = useCallback(async (memoryId) => {
    try {
      await deleteMemory(memoryId);
      addToast(t('settings.memory.memoryDeleted'), 'success');
    } catch (error) {
      console.error('Error deleting memory:', error);
      addToast(t('settings.memory.memoryDeleteFailed'), 'error');
    }
  }, [deleteMemory, addToast]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.text_color_primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SettingsSection
        title={t('pages.memory.useInConversations')}
        subtitle={t('pages.memory.useInConversationsDesc')}
      >
        <View style={styles.toggleSection}>
          <SwitchInput
            value={isMemoryEnabled}
            onValueChange={handleMemoryToggle}
            disabled={isUpdatingPreference}
          />
        </View>
      </SettingsSection>

      <SettingsSection
        title={t('pages.memory.learnFromConversations')}
        subtitle={isPlus
          ? t('settings.memory.learnFromConversationsDesc')
          : t('settings.memory.learnFromConversationsDescFree')}
      >
        <View style={styles.toggleSection}>
          <SwitchInput
            value={isPlus && isLearningEnabled}
            onValueChange={handleLearningToggle}
            disabled={isUpdatingLearning || !isMemoryEnabled}
          />
        </View>
        {!isPlus && (
          <Pressable onPress={handlePlusUpsell}>
            <Text size="xs" color="accent" style={styles.plusUpsell}>
              {t('settings.memory.learnPlusUpsell')}
            </Text>
          </Pressable>
        )}
      </SettingsSection>

      <SettingsSection
        title={t('settings.memory.title')}
        subtitle={t('settings.memory.subtitle')}
        isLast
        isColumn
      >
        {!isMemoryEnabled ? (
          <Text size="n" color="secondary" style={styles.emptyState}>
            {t('settings.memory.memoryDisabled')}
          </Text>
        ) : (
          <View style={styles.addMemorySection}>
            <View style={styles.inputWrapper}>
              <TextInput
                style={styles.memoryInput}
                value={newMemory}
                onChangeText={setNewMemory}
                placeholder={t('settings.memory.placeholder')}
                placeholderTextColor={colors.color_input_placeholder}
                maxLength={MEMORY_CHAR_LIMIT}
                multiline
                numberOfLines={3}
              />
              {(() => {
                const charProps = getCharCountProps(newMemory.length, MEMORY_CHAR_LIMIT);
                return (
                  <Text
                    size="xxs"
                    color={charProps.color}
                    style={[styles.characterCount, charProps.style]}
                  >
                    {newMemory.length} / {MEMORY_CHAR_LIMIT}
                  </Text>
                );
              })()}
            </View>
            <Button
              text={t('settings.memory.addMemory')}
              variant={!newMemory.trim() || newMemory.length > MEMORY_CHAR_LIMIT ? 'secondary' : 'primary'}
              isLoading={isCreating}
              isDisabled={!newMemory.trim() || newMemory.length > MEMORY_CHAR_LIMIT || isCreating}
              onPress={handleCreateMemory}
            />
          </View>
        )}
      </SettingsSection>

      <View style={styles.divider} />

      <SettingsSection
        title={t('settings.memory.yourMemories')}
        subtitle={memories.length > 0 ? t('settings.memory.memoriesStoredCount').replace('%COUNT%', memories.length) : t('settings.memory.noMemoriesYet')}
        isBold
        isColumn
        isLast
      >
        {fetchError ? <View style={{ gap: 12, paddingVertical: 20 }}>
          <Text accessibilityRole="alert" color="danger">{t('settings.memory.failedToLoad')}</Text>
          <Button text={t('common.retry')} onPress={() => fetchMemories().catch(() => {})} isLoading={isLoading} />
        </View> : memories.length === 0 ? (
          <Text size="n" color="secondary" style={styles.emptyState}>
            {t('settings.memory.noMemoriesStored')}
          </Text>
        ) : (
          <View style={styles.memoryList}>
            {memories.map((memory) => (
              <MemoryItem
                key={memory.id}
                memory={memory}
                onUpdate={handleUpdateMemory}
                onDelete={handleDeleteMemory}
                formatDate={formatDate}
              />
            ))}
          </View>
        )}
      </SettingsSection>
    </View>
  );
} 